import React, { useEffect, useState } from 'react';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { Box, Typography, Card, CardContent, Tabs, Tab, Divider } from '@mui/material';
import { getSettingTabs, getSettingData } from 'src/ApiServices';

// Tab Components imports
import CompanyInfoTab from './CompanyInfoTab';
import HomepageSEOTab from './HomepageSEOTab';
import StaticPagesSEOTab from './StaticPagesSEOTab';
import LogoTab from './LogoTab';
import SocialLinksTab from './SocialLinksTab';
import IntegrationsTab from './IntegrationsTab';
import EmailTemplatesTab from './EmailTemplatesTab';
import SecurityTab from './SecurityTab';

const Setting = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [tabsData, setTabsData] = useState([]);
  const [currentTabData, setCurrentTabData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tab sequence with IDs
  const TAB_SEQUENCE = [
    { id: 1, component: CompanyInfoTab, defaultLabel: 'Company Information' },
    { id: 2, component: HomepageSEOTab, defaultLabel: 'Homepage SEO' },
    { id: 3, component: StaticPagesSEOTab, defaultLabel: 'Static Pages SEO' },
    { id: 4, component: LogoTab, defaultLabel: 'Logo & Favicon' },
    { id: 5, component: SocialLinksTab, defaultLabel: 'Social Links' },
    { id: 6, component: IntegrationsTab, defaultLabel: 'Integrations' },
  ];

  // Fetch tabs data on component mount
  useEffect(() => {
    fetchTabsData();
  }, []);

  // Fetch data when active tab changes
  useEffect(() => {
    if (tabsData.length > 0) {
      const currentTab = tabsData[activeTab];
      if (currentTab.type_id !== 'email_templates') {
        fetchTabData(currentTab.type_id);
      } else {
        setCurrentTabData([]);
      }
    }
  }, [activeTab, tabsData]);

  const fetchTabsData = async () => {
    try {
      const response = await getSettingTabs();

      if (response.status === 200) {
        const apiTabsData = response.data.data;

        const dynamicTabs = TAB_SEQUENCE.map((tab) => {
          const apiTab = apiTabsData.find((apiTab) => apiTab.id === tab.id);
          return {
            label: apiTab ? apiTab.name : tab.defaultLabel,
            type_id: tab.id,
            component: tab.component
          };
        });

        // Add Email Templates tab
        const allTabs = [
          ...dynamicTabs,
          {
            label: 'Email Templates',
            type_id: 'email_templates',
            component: EmailTemplatesTab
          },
          {
            label: 'Security',
            type_id: 'security',
            component: SecurityTab
          }
        ];

        setTabsData(allTabs);
      }
    } catch (error) {
      console.error('Error fetching tabs:', error);
      setTabsData(getDefaultTabs());
    }
  };

  const fetchTabData = async (typeId) => {
    try {
      setLoading(true);
      console.log(`Fetching data for type_id: ${typeId}`);

      // Direct typeId pass karein
      const response = await getSettingData(typeId);

      if (response.status === 200) {
        setCurrentTabData(response.data.data || []);
        console.log(`Data received for type_id ${typeId}:`, response.data.data);
      }
    } catch (error) {
      console.error(`Error fetching data for type_id ${typeId}:`, error);
      setCurrentTabData([]);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultTabs = () => {
    return TAB_SEQUENCE.map((tab) => ({
      label: tab.defaultLabel,
      type_id: tab.id,
      component: tab.component
    })).concat([
      {
        label: 'Email Templates',
        type_id: 'email_templates',
        component: EmailTemplatesTab
      }
    ]);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Render current tab component with data
  const renderTabContent = () => {
    if (tabsData.length === 0) return <Typography>Loading tabs...</Typography>;

    if (loading) return <Typography>Loading data...</Typography>;

    const currentTab = tabsData[activeTab];
    const TabComponent = currentTab.component;

    if (currentTab.type_id === 'email_templates') {
      return <TabComponent />;
    }

    return <TabComponent tabData={currentTabData} typeId={currentTab.type_id} />;
  };

  return (
    <Page title={`Settings | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark,
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Settings
          </Typography>
        </Box>

        {/* Tabs Section */}
        <Card variant="outlined" sx={{ p: { xs: 1, md: 2 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              '& .MuiTabs-indicator': { backgroundColor: palette.primary.main },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: 'poppinsMedium'
              }
            }}
          >
            {tabsData.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>

          <Divider />

          {/* Tab Content */}
          <CardContent sx={{ pt: 2, minHeight: 400 }}>{renderTabContent()}</CardContent>

          <Divider />
        </Card>
      </Box>
    </Page>
  );
};

export default Setting;
